import { useState } from 'react';
import '@/App.css';
import { Toaster } from 'sonner';
import Landing from '@/components/Landing';
import LeadForm from '@/components/LeadForm';
import Quiz from '@/components/Quiz';
import QuizResult from '@/components/QuizResult';
import PhotoCapture from '@/components/PhotoCapture';
import PhotoGenerate from '@/components/PhotoGenerate';
import PhotoResult from '@/components/PhotoResult';
import PhotoLimit from '@/components/PhotoLimit';
import Footer from '@/components/Footer';
import { hasReachedLimit } from '@/utils/rateLimit';

function App() {
  const [screen, setScreen] = useState('landing');
  const [leadData, setLeadData] = useState({ name: '', phone: '', email: '' });
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizScore, setQuizScore] = useState(0);
  const [capturedImages, setCapturedImages] = useState([]);
  const [generatedPhotoUrl, setGeneratedPhotoUrl] = useState('');
  const [photoId, setPhotoId] = useState('');

  const resetAll = () => {
    setScreen('landing');
    setLeadData({ name: '', phone: '', email: '' });
    setQuizAnswers([]);
    setQuizScore(0);
    setCapturedImages([]);
    setGeneratedPhotoUrl('');
    setPhotoId('');
  };

  const handleLeadSubmit = (data, flow) => {
    setLeadData(data);
    if (flow === 'quiz') {
      setScreen('quiz-result');
    } else if (hasReachedLimit(data.email)) {
      setScreen('photo-limit');
    } else {
      setScreen('photo-capture');
    }
  };

  const handleQuizComplete = (score, answers) => {
    setQuizScore(score);
    setQuizAnswers(answers);
    setScreen('quiz-form');
  };

  const handlePhotoCaptured = (imageBlobs) => {
    setCapturedImages(imageBlobs);
    setScreen('photo-generate');
  };

  const handlePhotoGenerated = (url, id) => {
    setGeneratedPhotoUrl(url);
    setPhotoId(id);
    setScreen('photo-result');
  };

  const handlePhotoError = () => {
    setScreen('photo-capture');
  };

  return (
    <div className="min-h-screen" style={{ maxWidth: '480px', margin: '0 auto' }}>
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: '#1A1A1A',
            border: '1px solid rgba(181,152,90,0.3)',
            color: '#fff',
          },
        }}
      />

      {/* Persistent header: logo + headline */}
      <header className="px-6 pt-10 pb-8 text-center">
        <div className="flex items-center justify-center gap-6 mx-auto mb-4">
          <img
            src="/logo-c21.png"
            alt="Century 21"
            style={{ maxWidth: '140px' }}
          />
          <img
            src="/logo-kip.webp"
            alt="Kartódromo Internacional de Palmela"
            style={{ maxWidth: '105px' }}
          />
        </div>
        <h1
          className="font-headline text-2xl sm:text-3xl mb-2"
          style={{ color: '#FFFFFF', lineHeight: 1.2 }}
        >
          A sua próxima casa começa na pista.
        </h1>
        <p
          className="font-body text-sm"
          style={{ color: '#B5985A' }}
        >
          Patrocinador oficial do Kartódromo Internacional de Palmela
        </p>
      </header>

      {screen === 'landing' && (
        <Landing
          onQuizStart={() => setScreen('quiz')}
          onPhotoStart={() => setScreen('photo-form')}
        />
      )}

      {screen === 'quiz' && (
        <Quiz onComplete={handleQuizComplete} onBack={() => setScreen('landing')} />
      )}

      {screen === 'quiz-form' && (
        <LeadForm
          flow="quiz"
          score={quizScore}
          onSubmit={(data) => handleLeadSubmit(data, 'quiz')}
          onBack={() => setScreen('landing')}
        />
      )}

      {screen === 'quiz-result' && (
        <QuizResult
          score={quizScore}
          answers={quizAnswers}
          leadData={leadData}
          onReset={resetAll}
        />
      )}

      {screen === 'photo-form' && (
        <LeadForm
          flow="photo"
          onSubmit={(data) => handleLeadSubmit(data, 'photo')}
          onBack={() => setScreen('landing')}
        />
      )}

      {screen === 'photo-capture' && (
        <PhotoCapture
          onCapture={handlePhotoCaptured}
          onBack={() => setScreen('landing')}
        />
      )}

      {screen === 'photo-generate' && (
        <PhotoGenerate
          imageBlobs={capturedImages}
          userName={leadData.name}
          leadData={leadData}
          onGenerated={handlePhotoGenerated}
          onError={handlePhotoError}
          onLimitReached={() => setScreen('photo-limit')}
        />
      )}

      {screen === 'photo-result' && (
        <PhotoResult
          photoUrl={generatedPhotoUrl}
          photoId={photoId}
          userName={leadData.name}
          onReset={resetAll}
        />
      )}

      {screen === 'photo-limit' && (
        <PhotoLimit onReset={resetAll} />
      )}

      <Footer />
    </div>
  );
}

export default App;
